import React, { useState } from "react";
import {
  Card,
  Flex,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Avatar,
  Divider,
  Icon,
} from "@chakra-ui/react";
import { MdEmail, MdAccessTime, MdInfo } from "react-icons/md";

import BackButton from "../components/BackButton";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../routes/axiosInstance";
import { formatTimestampInDateAndTime } from "../logics/timeLogic";

function SettingPage() {
  const { logoutUser, currentUser, checkUserAuth } = useAuth();
  const [loading, setLoading] = useState(false);

  const logoutHandler = async () => {
    setLoading(true);
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
    };

    try {
      await axiosInstance.post("/api/users/logout", {}, config);
      logoutUser();
    } catch (error) {
      console.error("Logout error:", error.message);
      checkUserAuth(error.status);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <Flex align="center" spacing={3}>
            <BackButton />
            <Text fontSize="lg" fontWeight="600" color="gray.800" ml={3}>
              Settings
            </Text>
          </Flex>
        </Card>

        {/* Content */}
        <Box
          flex="1"
          bg="white"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          <VStack spacing={6} p={6} align="stretch">
            {/* Profile Section */}
            <VStack spacing={4} align="center">
              <Avatar
                name={currentUser.name}
                size="xl"
                fontSize="xl"
                fontWeight="600"
              />
              <VStack spacing={1} align="center">
                <Text fontSize="xl" fontWeight="600" color="gray.800">
                  {currentUser.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  User Profile
                </Text>
              </VStack>
            </VStack>

            <Divider />

            {/* User Information */}
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" fontWeight="600" color="gray.700">
                Account Information
              </Text>

              {/* Email */}
              <HStack spacing={3} p={3} bg="gray.50" borderRadius="lg">
                <Icon as={MdEmail} color="gray.500" boxSize={5} />
                <VStack align="start" spacing={0} flex="1">
                  <Text fontSize="sm" color="gray.500" fontWeight="500">
                    Email Address
                  </Text>
                  <Text fontSize="sm" color="gray.800">
                    {currentUser.email}
                  </Text>
                </VStack>
              </HStack>

              {/* Last Login */}
              {currentUser.lastLogin && (
                <HStack spacing={3} p={3} bg="gray.50" borderRadius="lg">
                  <Icon as={MdAccessTime} color="gray.500" boxSize={5} />
                  <VStack align="start" spacing={0} flex="1">
                    <Text fontSize="sm" color="gray.500" fontWeight="500">
                      Last Login
                    </Text>
                    <Text fontSize="sm" color="gray.800">
                      {formatTimestampInDateAndTime(currentUser.lastLogin)}
                    </Text>
                  </VStack>
                </HStack>
              )}
            </VStack>

            <Divider />

            {/* Actions */}
            <VStack spacing={4} align="stretch">
              <Button
                colorScheme="red"
                variant="outline"
                size="md"
                onClick={logoutHandler}
                isLoading={loading}
                loadingText="Logging out..."
                borderRadius="lg"
                _hover={{ bg: "red.50" }}
              >
                Logout
              </Button>
            </VStack>

            <Divider />

            {/* App Info */}
            <VStack spacing={3} align="center" pt={4}>
              <HStack spacing={2} align="center">
                <Icon as={MdInfo} color="teal.500" boxSize={4} />
                <Text fontSize="sm" fontWeight="600" color="teal.500">
                  VartaVerse v1.1.0
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                Secure messaging platform for seamless communication
              </Text>
            </VStack>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
}

export default SettingPage;
